import { Dropdown, Button, Tag, Tooltip } from 'antd';
import { MoreOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  COMMISSION_STATUS_COLORS,
  COMMISSION_STATUS_LABELS,
} from './offlineProductSales.constants';
import { formatCurrency, formatDate, formatPaidDateDisplay } from './offlineProductSales.helpers';

function EllipsisText({ text, className = '' }) {
  const value = text || '—';
  return (
    <Tooltip title={text || undefined}>
      <span className={`block truncate ${className}`.trim()}>{value}</span>
    </Tooltip>
  );
}

export function getOfflineProductSalesColumns({ canManage, onEdit, onDelete, onPartnerClick }) {
  const columns = [
    {
      title: 'Deal date',
      dataIndex: 'deal_date',
      key: 'deal_date',
      width: 118,
      ellipsis: true,
      render: (value) => <span className="text-gray-700 whitespace-nowrap">{formatDate(value)}</span>,
    },
    {
      title: 'Product type',
      dataIndex: 'product_type',
      key: 'product_type',
      width: 100,
      ellipsis: true,
      render: (text) => <EllipsisText text={text} className="font-medium text-gray-900" />,
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer_name',
      key: 'buyer_name',
      width: 140,
      ellipsis: true,
      render: (text) => <EllipsisText text={text} className="text-gray-800" />,
    },
    {
      title: 'Buyer PAN',
      dataIndex: 'buyer_pan',
      key: 'buyer_pan',
      width: 120,
      ellipsis: true,
      render: (text) => (
        <EllipsisText
          text={text}
          className="font-mono text-xs text-blue-700"
        />
      ),
    },
    {
      title: 'ISIN',
      dataIndex: 'isin',
      key: 'isin',
      width: 130,
      ellipsis: true,
      render: (text) => (
        <EllipsisText text={text} className="font-mono text-xs text-gray-600" />
      ),
    },
    {
      title: 'Partner Name',
      key: 'ifa',
      width: 130,
      ellipsis: true,
      render: (_, record) => {
        const name = record?.ifa?.name;
        const uuid = record?.ifa?.uuid;
        if (!name) return <EllipsisText text={null} className="text-gray-800 font-medium" />;
        if (!uuid || !onPartnerClick) {
          return <EllipsisText text={name} className="text-gray-800 font-medium" />;
        }
        return (
          <Tooltip title={`View ${name} profile`}>
            <button
              type="button"
              className="block w-full truncate text-left font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onPartnerClick(record);
              }}
            >
              {name}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: 'Security',
      dataIndex: 'security_name',
      key: 'security_name',
      width: 200,
      ellipsis: true,
      render: (text) => <EllipsisText text={text} className="text-gray-600" />,
    },
    {
      title: 'RM',
      key: 'rm',
      width: 110,
      ellipsis: true,
      render: (_, record) => (
        <EllipsisText text={record?.rm?.name} className="text-green-700 font-medium" />
      ),
    },
    {
      title: 'Altcase revenue',
      key: 'altcase_revenue',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <span className="font-semibold text-gray-900 whitespace-nowrap">
          {formatCurrency(record.altcase_revenue)}
        </span>
      ),
    },
    {
      title: 'Commission status',
      dataIndex: 'commission_paid_status',
      key: 'commission_paid_status',
      width: 130,
      render: (status) => {
        if (!status) return <span className="text-gray-400">—</span>;
        return (
          <Tag color={COMMISSION_STATUS_COLORS[status] || 'default'} className="m-0 border-0 font-medium px-2 rounded">
            {COMMISSION_STATUS_LABELS[status] || status}
          </Tag>
        );
      },
    },
    {
      title: 'Paid date',
      key: 'paid_date',
      width: 140,
      ellipsis: true,
      render: (_, record) => {
        const display = formatPaidDateDisplay(record.paid_date, record.paid_date_note);
        const fullNote = !record.paid_date && record.paid_date_note ? record.paid_date_note : null;
        return (
          <Tooltip title={fullNote || undefined}>
            <span className="block truncate text-gray-600">{display}</span>
          </Tooltip>
        );
      },
    },
  ];

  if (canManage) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  onEdit(record);
                },
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  onDelete(record);
                },
              },
            ],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    });
  }

  return columns;
}
